// @flow
import React, {useEffect, useState} from 'react';
import {useHistory} from "react-router-dom";
import Header from "../Header";
import BackButton from "../../GUI_COMMON/COMPONENTS/BackButton";
import TextInput from "../../GUI_COMMON/COMPONENTS/TextInput";
import LIButton from "../../GUI_COMMON/COMPONENTS/LIButton";
import Popup from "../../GUI_COMMON/COMPONENTS/Popup";
import ArchiverClientApi from "../../API/archiverClientApi";
import LoadingSpinner from "../../GUI_COMMON/COMPONENTS/LoadingSpinner";
import ArchiverGuiUtils from "../../SUPPORT/ArchiverGuiUtils";
import LIUtils from "../../GUI_COMMON/SUPPORT/LIUtils";

const MagRegisterAdminPage = () => {

    const [fullDeviceList, setFullDeviceList] = useState([]);
    const [fullProfilesList, setFullProfilesList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDeviceId, setSelectedDeviceId] = useState(-1);
    const [magBarcode, setMagBarcode] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const history = useHistory();

    useEffect(() => {
        console.debug("refreshing device list");
        doGetDeviceList();
    }, []);
    useEffect(() => {
        if(selectedDeviceId>0) {
            // we need the mag profiles only
            setFullProfilesList(fullDeviceList.find(item => item.id===selectedDeviceId).profiles??[]);
        }
        else {
            setFullProfilesList([]);
        }
    }, [selectedDeviceId,fullDeviceList]);
    
    
    function doGetDeviceList() {
        setIsLoading(true);
        ArchiverClientApi.getMagableDevices() 
        .then( (devices) => {
            // filter out the system level devices (these don't count)
            setFullDeviceList(devices);
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => {
            setIsLoading(false);
        });
    }

    function dumpDeviceList() {
        if(isLoading) {
            return <LoadingSpinner medium/>;
        }
        return(
            <>
                <label htmlFor="deviceId" className="li-form-label mr-3 d-block">Register to:</label>
                <select onChange={onChange} id="deviceId" name="deviceId" value={selectedDeviceId??-1}
                        className="li-form-field no-width align-self-end li-border-dark" required={true}>
                    <option value={-1} disabled>(select a device)</option>
                    <option value={0}>No Device (unregister/create)</option>
                    {dumpDeviceOptionsList()}
                </select>
            </>
        );
    }

    function dumpDeviceOptionsList() {
        return fullDeviceList.map(item => <option key={item.id} value={item.id}>{item.fullName}</option>);
    }
    
    function dumpDeviceProfileList() {
        if(isLoading) {
            return <LoadingSpinner medium/>;
        }
        return(
            <>
                <label htmlFor="deviceId" className="li-form-label mr-3">Device Profiles:</label>
                <table className="table table-sm table-striped table-bordered">
                    <tbody>
                        {dumpCurrentDeviceProfiles()}
                    </tbody>
                </table>
            </>
        );
    }
    
    function dumpCurrentDeviceProfiles() {
        if(fullProfilesList.length===0) {
            if(selectedDeviceId===-1) {
                return <tr><td>(select a device)</td></tr>;
            }
            if(selectedDeviceId===0) {
                return <tr><td>-</td></tr>;
            }
            return <tr><td>(no mag profiles for this device?)</td></tr>;
        }
        return fullProfilesList.map(item => {
            if(item.type===0) { // mag type profile
                const validations = LIUtils.FindValidationsGivenIRs(item.inspectionRegions)??[];
                const validationsStr = validations.join(", ");
                return <tr key={item.id}>
                    <td>{item.name}</td>
                    <td className="w-auto"><i className="fa fa-arrow-right"></i></td>
                    <td>{validationsStr}</td>
                </tr>;
            }
            return null;
        });
    }

    function onChange(e) {
        const thing = e.target.name;

        let value = e.target.value;
        if (thing === "deviceId") {  // number
            setSelectedDeviceId(Number(value));
        }
        else if(thing==="magBarcode") {
            setMagBarcode(value);
        }
    }
    
    function isValid() {
        if(!magBarcodeLooksGood(magBarcode)) {
            return false;
        }
        if(fullProfilesList.length===0 && selectedDeviceId>0) {
            return false;
        }
        return selectedDeviceId !== -1; 
    }
    
    function magBarcodeLooksGood(magBC) {
        if(!magBC || magBC==="") {
            return false;
        }
        const re = new RegExp('^[0-9][0-9][a-zA-Z]*.[0-9]{4}.[0-9]$');
        if(!re.test(magBC)) { // not complete
            console.log("bad mag barcode "+magBC);
            return false;
        }
        return true; // good
    }

    function handleRegisterClicked(e) {
        e.preventDefault();
        e.stopPropagation();

        setIsSaving(true);
        ArchiverClientApi.sendRegisterMagToDevice(magBarcode, selectedDeviceId)
        .then((magInfo) => {
            Popup.showInfo(`Mag '${magBarcode}' registered to '${magInfo.parentDeviceName??"no specific device"}'.`);
        })
        .catch(responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally(() => {
            setIsSaving(false);
        });
    }
    
    function handleBackClicked() {
        history.goBack();
    }
    
    return (
        <div className="container">
            <Header hideAdmin/>
            <div className="li-header-row">
                <BackButton onClick={handleBackClicked}/>
                <h1 className="ml-4 d-inline-block align-middle">Mag Register/Unregister</h1>
            </div>
            <p>Use this page to register new magazines into the system.  You don't ordinarily need to do this yourself -- when you print labels, the system automatically registers
                the magazine to the device based on the box number that is currently being worked on.  </p>
            <p>If you find that a magazine has been skipped or is otherwise missing, you can use this page to force a magazine to be registered to a particular machine, so that
                labels can then be printed for it.</p>
            <ul>
                <li>It is likely that any newly registered magazine will become the "NEXT" magazine required for insertion into the parent device.  If you don't want this, choose the 'No Device' option
                    from the list below.</li>
                <li>You can unregister a magazine from its parent device by selecting "No Device" from the list below.</li>
                <li>Only magazines that have been skipped or are for PAST years should be registered
                using this page.</li>
            </ul>
            <hr/>
            <div className="li-border-round li-border-dark p-2">
                <div className="row px-3 pb-3 li-bg-light">
                    <TextInput onChange={onChange} label="Magazine Barcode" name="magBarcode" widthPels={600} oneRow placeholder="##<neumonic>.####.#"
                               helpText="* this may be a new magazine or one that is already registered to another device"/>
                    <p className="mt-1"><strong className="li-fg-danger">** DO NOT register magazines that are for the future 
                        (use the <a href="/app/print">Print Labels</a> page to do this)</strong></p>
                </div>
                {/*<hr/>*/}
                <div className="row justify-content-around">
                   <div className="col">
                        {dumpDeviceList()}
                   </div>
                   <div className="col">
                       {dumpDeviceProfileList()}
                   </div>    
                </div>
            </div>
            
            <div className="text-center mt-5">
                <LIButton bgClass="" primary onClick={handleRegisterClicked} label={selectedDeviceId!==0?"Register":"Unregister/Create"} 
                          showBusy={isSaving} disabled={!isValid()}/>
            </div>
        </div>
    );
}

MagRegisterAdminPage.propTypes = {
   
};

export default MagRegisterAdminPage;